import {Injectable} from '@angular/core';
import {ICenterRoles, IJwtValidatedUser, IPermission, queryCondition} from '@evencel/models';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {DarkMode, HandleTokenResponse, ShowLoading, UpdateCenterRoles, UpdateDirtyFormIndex} from './app.actions';
import {decodeToken} from './app.state.utils';

export interface AppStateModel {
    loading: boolean;
    darkMode: boolean;
    matDrawerOpened: boolean;
    accessToken?: string;
    refreshToken?: string;
    user?: IJwtValidatedUser;
    centerRoles?: ICenterRoles[];
    policies?: IPermission[];
    dirtyFormIndex: number;
}

export const APP_STATE_DEFAULT = {
    loading: false,
    darkMode: false,
    matDrawerOpened: true,
    dirtyFormIndex: -1,
};

@State<AppStateModel>({
    name: 'app',
    defaults: APP_STATE_DEFAULT,
})
@Injectable({providedIn: 'root'})
export class AppState {
    @Selector() static loading(state: AppStateModel) {
        return state.loading;
    }

    @Selector() static accessToken(state: AppStateModel) {
        return state.accessToken;
    }

    @Selector() static durtyFormIndex(state: AppStateModel) {
        return state.dirtyFormIndex;
    }

    @Selector() static policies(state: AppStateModel) {
        return state.policies;
    }

    @Selector() static centers(state: AppStateModel) {
        return state.centerRoles?.map(cr => cr.center.acronym);
    }

    @Selector() static currentCenter(state: AppStateModel) {
        return state.centerRoles?.[0].center;
    }

    @Selector() static refreshToken(state: AppStateModel) {
        return state.refreshToken;
    }

    @Selector() static user(state: AppStateModel) {
        return state.user;
    }

    @Selector() static userId(state: AppStateModel) {
        return state.user?._id;
    }

    @Selector() static darkMode(state: AppStateModel) {
        return state.darkMode;
    }

    @Selector() static abilities(state: AppStateModel) {
        const userWithPolicies = {
            ...state.user,
            policies: state.policies,
        } as IJwtValidatedUser;

        return queryCondition(userWithPolicies);
    }

    @Action(ShowLoading)
    showLoading({patchState}: StateContext<AppStateModel>, {loading}: ShowLoading) {
        return patchState({loading});
    }

    @Action(UpdateCenterRoles)
    updateCenterRoles({patchState}: StateContext<AppStateModel>, {centerRoles}: UpdateCenterRoles) {
        const policies = centerRoles.roles.flatMap(e => e.permissions);
        return patchState({centerRoles: [centerRoles], policies});
    }

    @Action(UpdateDirtyFormIndex)
    updateDirtyFormIndex({patchState}: StateContext<AppStateModel>, {index}: UpdateDirtyFormIndex) {
        return patchState({dirtyFormIndex: index});
    }

    @Action(HandleTokenResponse)
    setAccessToken(
        {patchState}: StateContext<AppStateModel>,
        {response: {accessToken, refreshToken}}: HandleTokenResponse
    ) {
        const user = decodeToken(accessToken);
        const toPatch = {
            accessToken,
            user,
            refreshToken,
            showSideNav: true,
        };

        return patchState(toPatch);
    }

    @Action(DarkMode)
    darkMode({patchState, getState}: StateContext<AppStateModel>) {
        const darkMode = !getState().darkMode;
        return patchState({darkMode});
    }
}
